import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Align from '@marvelapp/marvel-ui/align';
import Button from '@marvelapp/marvel-ui/button';
import Grid, { GridItem } from '@marvelapp/marvel-ui/grid';
import Heading from '@marvelapp/marvel-ui/heading';
import Link from '@marvelapp/marvel-ui/link';
import LogoRow from '@marvelapp/marvel-ui/logo-row';
import MaxWidth from '@marvelapp/marvel-ui/max-width';
import PageWrap from '@marvelapp/marvel-ui/page-wrap';
import Paragraph from '@marvelapp/marvel-ui/paragraph';
import Spacing from '@marvelapp/marvel-ui/spacing';

import Page from 'marketing-website/components/modules/Page';
import CaseStudies from 'marketing-website/components/modules/CaseStudies';
import WelcomeMessage from 'marketing-website/components/modules/WelcomeMessage';
import PowerfulDesign from 'marketing-website/components/modules/PowerfulDesign';
import IntegrationsCta from 'marketing-website/components/modules/IntegrationsCta';

import { isUserLoggedIn, getUsername } from 'marketing-website/selectors';
import metaData from 'marketing-website/data/metadata.js';
import { getNextSignupRedirect } from 'marketing-website/utils/forms';

import assets from 'marketing-website/configs/pages/homepage/assets';
import Email from 'marketing-website/components/icons/Email';
import MarvelEnterprise from 'marketing-website/components/modules/MarvelEnterprise';
import Image from 'marketing-website/components/modules/Image';
import logos from 'marketing-website/configs/pages/homepage/logos';
import MarvelThreeImg from 'marketing-website/media/images/pages/user-testing/marvel-three-dashboard@2x.jpg';
import { isIOSOrIPadOS } from 'marketing-website/utils/deviceDetection';

import GoogleButton from '../components/modules/FormWrap/GoogleButton';
import generalStyles from './style/general.module.css';

// Temporary workaround to disable Google Sign in on iOS/iPadOS and server-side
// when generating the HTML template
const showGoogleSign = !isIOSOrIPadOS() && !!process.env.CLIENT;

function Homepage({ isLoggedIn, username, data }) {
  if (typeof window !== `undefined` && window.ga) {
    window.ga('send', 'pageview', isLoggedIn ? '/return-to-dashboard/' : '/');
  }

  const schema = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Marvel',
    alternateName: 'Marvel App',
    description:
      'Marvel is an all-in-one design platform, which includes rapid prototyping, user testing and designer to developer code handoff, created for modern design teams.',
    url: 'https://marvelapp.com/',
    email: 'sales@marvelapp.com',
    logo: 'https://marvelapp.com/wp-content/uploads/2018/06/Logo-Light.png',
    sameAs: [
      'https://www.facebook.com/marvelapp',
      'https://twitter.com/marvelapp',
      'https://www.instagram.com/marvelapp/',
      'https://www.youtube.com/channel/UCPyHK9eJgCs-fkq4yxc1EXw?pbjreload=102',
      'https://www.linkedin.com/company/marvel-app/',
      'https://github.com/marvelapp',
      'https://marvelapp.com/',
    ],
  });

  const pageProps = {
    title: metaData.homepage.title,
    meta: [
      {
        name: 'description',
        content: metaData.homepage.description,
      },
      {
        name: 'keywords',
        content: metaData.homepage.keywords,
      },
    ],
    header: true,
    footer: true,
    banner: true,
    schema,
  };

  const redirectTo = getNextSignupRedirect();
  return (
    <Page {...pageProps}>
      <div className={generalStyles.page}>
        <Spacing size="l" sizeM="xl" position="top" type="padding">
          <PageWrap size="l">
            <Spacing type="padding" size="xl" position="topBottom">
              <Grid columns={12} alignItems="center">
                <GridItem columnSpan={12} columnSpanM={5}>
                  <Align type="center" typeM="left">
                    <MaxWidth value={480} display="inlineBlock">
                      <Heading tag="h1" size="xxxxl" weight="semibold">
                        Rapid prototyping, testing and handoff for modern design
                        teams
                      </Heading>
                      <Spacing type="margin" size="xs" position="top">
                        <Paragraph size="l">
                          Marvel has everything you need to bring ideas to life
                          and transform how you create digital products with
                          your team. Placing the power of design in everyone’s
                          hands.
                        </Paragraph>
                      </Spacing>
                    </MaxWidth>
                    {isLoggedIn ? (
                      <WelcomeMessage username={username} />
                    ) : (
                      <Spacing type="padding" size="l" position="topBottom">
                        <div className="display-flex flexWrap-wrap justifyContent-center breakPointM-justifyContent-flexStart">
                          <div className="breakPointS-marginRight-s marginBottom-s">
                            <Link url="/signup" openInNewTab={false}>
                              <Button
                                background="blue"
                                color="white"
                                size="large"
                                shape="rounded"
                                minWidth={240}
                                iconColor="white"
                                icon={<Email />}
                              >
                                Sign up free with email
                              </Button>
                            </Link>
                          </div>
                          {showGoogleSign && (
                            <div>
                              <GoogleButton
                                shape="rounded"
                                redirectTo={redirectTo}
                                siteMaintenance={
                                  data.site.siteMetadata.siteMaintenance
                                }
                              >
                                Sign up free with Google
                              </GoogleButton>
                            </div>
                          )}
                        </div>
                      </Spacing>
                    )}
                  </Align>
                </GridItem>
                <GridItem columnSpan={12} columnSpanM={7}>
                  <Image
                    alt="The all-in-one design platform powering creatives"
                    maxWidth={assets.hero.maxWidth}
                    src={assets.hero.src}
                    hasShadow={false}
                    useImageTag
                  />
                </GridItem>
              </Grid>
            </Spacing>
          </PageWrap>
        </Spacing>
        <Spacing size="m" position="topBottom" type="margin">
          <LogoRow logos={logos} />
        </Spacing>
        <PowerfulDesign />
        <CaseStudies />
        <MarvelEnterprise
          buttonText="Learn more"
          copy="Built to help large teams create amazing products at scale. Marvel Enterprise 3 is built on blazing fast technology with features that focus on helping large teams make design more productive."
          image={
            <Image
              alt="Marvel Enterprise Dashboard"
              src={MarvelThreeImg}
              maxWidth={700}
              hasShadow={false}
            />
          }
          title="Scale Design with Marvel Enterprise 3"
        />
        <IntegrationsCta />
      </div>
    </Page>
  );
}

export const HOME_PAGE_QUERY = graphql`
  query HomePageQuery {
    site {
      siteMetadata {
        siteMaintenance
      }
    }
  }
`;

Homepage.propTypes = {
  isLoggedIn: PropTypes.bool.isRequired,
  username: PropTypes.string,
  data: PropTypes.shape({
    site: PropTypes.shape({
      siteMetadata: PropTypes.shape({
        siteMaintenance: PropTypes.bool.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
};

function mapStateToProps(state) {
  return {
    isLoggedIn: isUserLoggedIn(state),
    username: getUsername(state),
  };
}

export default connect(mapStateToProps)(Homepage);



// WEBPACK FOOTER //
// ./src/pages/index.js